import type { Target } from 'src/models/TimerTarget';
import type { HydraCollection } from 'src/types';

import * as Sentry from '@sentry/browser';
import { useApiRead } from 'api-read-hook';
import { differenceInSeconds, parseISO } from 'date-fns';
import * as React from 'react';
import { Api, useApiCallable } from 'src/api';
import useInterval from 'src/hooks/use-interval';
import { Model } from 'src/models';

/**
 * Read once for the whole app, and invalidated exactly here after a start or a
 * stop, so every timer on the page moves together.
 */
const RECENT_PATH = '/work-dones/recent';

type Entry = Api.WorkDone.V_Timer;

type Timer = {
  /** The entry being timed right now, if there is one. */
  running: Entry | null;
  runningTarget: Target | null;
  /** Finished entries, most recent first. */
  logged: Array<Model.WorkDone.Logged<Entry>>;
  /** What the last stopped entry was against, i.e. what Start would resume. */
  lastTarget: Target | null;
  /**
   * The category that entry carried.  Resuming client time has to repeat it,
   * since the API will not take a company timer without one.
   */
  lastCategory: string | null;
  /** True while a start or stop is in flight, so controls can be held shut. */
  isBusy: boolean;
  isLoading: boolean;
  error: Error | undefined;
  /**
   * Why the last start or stop did not take, ready to show.  Nothing awaits
   * those calls, so without this a failure would show only as the control
   * quietly re-enabling.
   */
  actionError: string | null;
  /** Whether the clock is running against this exact target. */
  isRunning: (target: Target) => boolean;
  /** Starts fresh work, banking whatever is running first. */
  start: (target: Target, category?: string | null) => Promise<void>;
  stop: () => Promise<void>;
  subscribe: (onChange: () => void) => () => void;
};

const TimerContext = React.createContext<Timer | null>(null);
const ElapsedContext = React.createContext(0);

/**
 * The signed-in user's timer: what is running, what they were on recently, and
 * the controls to move between them.
 */
export function useTimer(): Timer {
  const timer = React.useContext(TimerContext);
  if (!timer) throw new Error('useTimer called outside a TimerProvider');
  return timer;
}

/**
 * Seconds on the running clock, ticking once a second.  Kept apart from the
 * timer itself so only what shows a clock re-renders on the tick.
 */
export function useTimerElapsed(): number {
  return React.useContext(ElapsedContext);
}

/**
 * Runs `onChange` after any start or stop, for anything outside the timers
 * which the change makes stale.
 */
export function useTimerChange(onChange: () => void): void {
  const { subscribe } = useTimer();
  const latest = React.useRef(onChange);
  latest.current = onChange;

  React.useEffect(() => subscribe(() => latest.current()), [subscribe]);
}

type Props = {
  /**
   * Timers are the agency's own and `/work-dones/recent` is ROLE_AGENCY only,
   * so clients get a timer which never fetches and never has anything running.
   */
  enabled: boolean;
  children: React.ReactNode;
};

export default function TimerProvider({ enabled, children }: Props) {
  const callApi = useApiCallable();
  const api = useApiRead<HydraCollection<Entry>>(enabled ? RECENT_PATH : null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const listeners = React.useRef(new Set<() => void>()).current;

  const entries = api.data?.['hydra:member'] ?? [];
  const running = entries.find(Model.WorkDone.isRunning) ?? null;
  const logged = Model.WorkDone.filterLogged(entries);
  const runningTarget = running
    ? Model.TimerTarget.fromWorkDone(running)
    : null;

  const subscribe = React.useCallback(
    (onChange: () => void) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    [listeners]
  );

  async function run(
    what: 'started' | 'stopped',
    action: () => Promise<unknown>
  ) {
    if (isBusy) return;
    setIsBusy(true);
    setActionError(null);
    try {
      await action();
      api.invalidateExact(RECENT_PATH);
      listeners.forEach((onChange) => onChange());
    } catch (err) {
      // Held rather than thrown on, since nothing awaits a start or a stop and
      // the rejection would go nowhere.  Still reported, as it would have been.
      Sentry.captureException(err);
      setActionError(`The timer could not be ${what}. Try again.`);
    } finally {
      setIsBusy(false);
    }
  }

  const stopEntry = (id: string) =>
    callApi(`/work-dones/${id}/stop`, { method: 'POST', jsonBody: {} });

  const timer: Timer = {
    running,
    runningTarget,
    logged,
    lastTarget: logged[0] ? Model.TimerTarget.fromWorkDone(logged[0]) : null,
    lastCategory: logged[0]?.categoryString || null,
    isBusy,
    actionError,
    isLoading: enabled && !api.data && !api.error,
    error: api.error,
    isRunning: (target) => runningTarget?.iri === target.iri,
    subscribe,

    // Only one timer can run at a time, so switching work banks the current
    // stretch before the new one starts.
    start: (target, category = null) =>
      run('started', async () => {
        if (running) await stopEntry(running.id);
        await callApi('/work-dones/start', {
          method: 'POST',
          jsonBody: Model.TimerTarget.getStartBody(target, category),
        });
      }),

    stop: () =>
      running ? run('stopped', () => stopEntry(running.id)) : Promise.resolve(),
  };

  return (
    <TimerContext.Provider value={timer}>
      <ElapsedProvider startedAt={running?.loggingStartedAt ?? null}>
        {children}
      </ElapsedProvider>
    </TimerContext.Provider>
  );
}

/**
 * Holds the ticking clock below the timer itself, so a second passing only
 * re-renders the components reading it rather than the whole app.
 */
function ElapsedProvider({
  startedAt,
  children,
}: {
  startedAt: string | null;
  children: React.ReactNode;
}) {
  const [now, setNow] = React.useState(() => new Date());

  // Only tick while something is actually being timed.
  useInterval(() => setNow(new Date()), startedAt ? 1000 : null);

  // Without this the clock would read from whenever it last ticked, which for
  // a timer started after a quiet spell is however long the app has been open.
  React.useEffect(() => setNow(new Date()), [startedAt]);

  const elapsedSeconds = startedAt
    ? Math.max(differenceInSeconds(now, parseISO(startedAt)), 0)
    : 0;

  return (
    <ElapsedContext.Provider value={elapsedSeconds}>
      {children}
    </ElapsedContext.Provider>
  );
}
